import { showApiError, showSuccess } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getUserDetails, updateUser } from "@/sideEffects/api";
import { FormSchemaType, UserMetaForm } from "../form/UserMetaForm";
import { LoadingSmall } from "@/components/LoadingSmall";

interface Props {
  userId: number;
}

export const UpdateUserMetaForm: React.FC<Props> = ({ userId }) => {
  const { t } = useTranslation();
  const { data: userDetails, isLoading } = useQuery({
    queryKey: ["getUserDetails", userId],
    queryFn: () => getUserDetails(userId),
  });

  const queryClient = useQueryClient();
  const updateUserMutation = useMutation({
    mutationFn: (values: FormSchemaType) =>
      updateUser({
        id: userId,
        name: values.name,
        surname: values.surname,
        email: values.email,
        gender: values.gender,
        mobile: values.mobile,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getUser"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getUserDetails", userId],
      });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  const user = userDetails?.data;
  if (!user || isLoading) {
    return (
      <div className="flex justify-center items-center h-full p-16">
        <LoadingSmall />
      </div>
    );
  }

  return (
    <>
      <UserMetaForm
        emailDisabled={true}
        buttonText={t("common.update")}
        onSubmit={(values) => updateUserMutation.mutate(values)}
        isLoading={updateUserMutation.isPending}
        defaultValues={{
          name: user.name,
          surname: user.surname,
          email: user.email,
          gender: user.gender ? user.gender.toString() : "",
          mobile: user.mobile,
          userId: user.id,
        }}
      />
    </>
  );
};
